import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export interface IAddLoadingOptions {
    key?: string | string[] | null;
}

export type IRemoveLoadingOptions = IAddLoadingOptions;
export type IGetLoadingOptions = IAddLoadingOptions;

const DEFAULT_KEY = '__default__';

@Injectable({ providedIn: 'root' })
export class IsLoadingService {
    private readonly counters = new Map<string, BehaviorSubject<number>>();

    add(options?: IAddLoadingOptions): void {
        this._getKeys(options).forEach(key => {
            const s = this._getOrCreate(key);
            s.next(s.value + 1);
        });
    }

    remove(options?: IRemoveLoadingOptions): void {
        this._getKeys(options).forEach(key => {
            const s = this._getOrCreate(key);
            s.next(Math.max(0, s.value - 1));
        });
    }

    clear(options?: IAddLoadingOptions): void {
        this._getKeys(options).forEach(key => {
            this._getOrCreate(key).next(0);
        });
    }

    isLoading$(options?: IGetLoadingOptions): Observable<boolean> {
        const keys = this._getKeys(options);
        const streams = keys.map(key => this._getOrCreate(key));
        if (streams.length === 1) {
            return streams[0].pipe(map(n => n > 0), distinctUntilChanged());
        }
        return combineLatest(streams).pipe(
            map(counts => counts.some(n => n > 0)),
            distinctUntilChanged()
        );
    }

    private _getOrCreate(key: string): BehaviorSubject<number> {
        if (!this.counters.has(key)) {
            this.counters.set(key, new BehaviorSubject<number>(0));
        }
        return this.counters.get(key)!;
    }

    private _getKeys(options?: IAddLoadingOptions): string[] {
        if (!options?.key) return [DEFAULT_KEY];
        return Array.isArray(options.key) ? options.key : [options.key];
    }
}
